import { useEffect, useState } from 'react';
import styles from './Avatar.module.css';

/**
 * A person, as a circle.
 *
 * Design system § 6 reserves the pure circle for the domain of sound and of
 * PEOPLE, and names the avatar among the four things allowed to take it. That
 * is the whole of this component's visual argument: everything else about it
 * — the fallback fill, the initial, the sizes — follows from the rules already
 * written down.
 *
 * The fallback is an INITIAL, not a drawn figure. § 10 puts the system
 * entirely in geometry and typography and § 16 leaves illustration undesigned,
 * so a placeholder silhouette would be a decision this library is not allowed
 * to take. A letter is typography, which is a decision already taken.
 *
 * `name` is required even when `image` is present: it is the accessible name
 * either way, and an avatar that is only a picture says nothing to a reader
 * who cannot see it (§ 12).
 */

interface AvatarProps {
  /** The person's name. Its first letter is the fallback, and it names the image. */
  name: string;
  /** Their picture, when the account carries one. */
  image?: string | null;
  /**
   * `sm` on a dense row, `md` everywhere a person is a label, `lg` on the one
   * screen a person is the subject (the profile's head). A fixed set rather
   * than a number — Avatar.module.css records why.
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * The first letter of the first word that has one.
 *
 * `Array.from` rather than `name[0]`, so a name opening on an astral character
 * yields that character and not half of its surrogate pair. Uppercased for the
 * same reason every other initial in the product is: it is a monogram, and
 * § 5.2's "no uppercase above 12px" governs RUNNING text, not a single glyph
 * standing in for a person.
 */
function initial(name: string): string {
  const first = Array.from(name.trim())[0];
  return first ? first.toLocaleUpperCase() : '';
}

export function Avatar({ name, image, size = 'md' }: AvatarProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [image]);

  if (image && !failed) {
    return (
      <img
        className={styles.avatar}
        data-size={size}
        src={image}
        alt={name}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className={styles.avatar} data-size={size} data-fallback="true" role="img" aria-label={name}>
      <span className={styles.initial} aria-hidden="true">
        {initial(name)}
      </span>
    </span>
  );
}
